import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft, ArrowRight, Heart, Briefcase, Sparkles, Loader2, CheckCircle,
  User, Mail, Lock, Phone, FileText, Clock, Users, Camera, Upload, X, File
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const profTypes = [
  { value: 'therapist', label: 'Terapeuta', desc: 'Psicólogo, psicanalista, psicoterapeuta', icon: Heart, color: 'bg-[#6B8E7C]' },
  { value: 'coach', label: 'Mentor', desc: 'Coach, mentor de carreira e vida', icon: Briefcase, color: 'bg-[#D4A373]' },
  { value: 'spiritualist', label: 'Espiritualista', desc: 'Tarot, reiki, terapias holísticas', icon: Sparkles, color: 'bg-[#9B8BAE]' }
];

const allSpecializations = {
  therapist: ['Ansiedade', 'Depressão', 'Relacionamentos', 'Autoestima', 'Trauma', 'Luto', 'Estresse', 'TOC', 'TDAH', 'Terapia de Casal', 'Infantil'],
  coach: ['Carreira', 'Liderança', 'Produtividade', 'Finanças Pessoais', 'Propósito de Vida', 'Inteligência Emocional', 'Comunicação'],
  spiritualist: ['Tarot', 'Reiki', 'Cristaloterapia', 'Meditação Guiada', 'Constelação Familiar', 'Florais de Bach', 'Yoga Terapêutico', 'Ayurveda', 'Acupuntura', 'Aromaterapia']
};

const allApproaches = {
  therapist: ['Cognitivo-Comportamental (TCC)', 'Psicanálise', 'Humanista', 'Gestalt', 'Sistêmica', 'EMDR', 'Mindfulness', 'ACT'],
  coach: ['Coaching Ontológico', 'PNL', 'Coaching Integral', 'Coaching Executivo', 'Mentoria'],
  spiritualist: ['Tarot Terapêutico', 'Tarot Evolutivo', 'Terapias Integrativas', 'Abordagem Energética', 'Práticas Meditativas', 'Naturopatia']
};

const regSteps = [
  { id: 'basic', title: 'Dados Básicos' },
  { id: 'type', title: 'Especialidade' },
  { id: 'qualifications', title: 'Qualificações' },
  { id: 'practice', title: 'Prática' },
  { id: 'pricing', title: 'Preços' }
];

export default function RegisterProfessionalPage() {
  const navigate = useNavigate();
  const { registerProfessional } = useAuth();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const photoInputRef = useRef(null);
  const certInputRef = useRef(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [certUploading, setCertUploading] = useState(false);

  const [form, setForm] = useState({
    name: '', email: '', password: '', confirmPassword: '', phone: '',
    professional_type: '',
    specializations: [], approach: [],
    bio: '', experience_years: '',
    experience_cases: '',
    session_preference: 'online',
    goal_methodology: '',
    open_collaboration: false,
    hourly_rate: '',
    session_prices: { chat: '', video: '', call: '', email: '' },
    photo_url: '',
    certificates: []
  });

  const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const toggleArray = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].includes(value) ? prev[field].filter(v => v !== value) : [...prev[field], value]
    }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const preview = URL.createObjectURL(file);
    setPhotoPreview(preview);
    setPhotoUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API_URL}/api/upload/photo`, formData);
      setField('photo_url', data.url);
      toast.success('Foto enviada!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar foto');
      setPhotoPreview(null);
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleCertUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setCertUploading(true);

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const { data } = await axios.post(`${API_URL}/api/upload/certificate`, formData);
        setForm(prev => ({
          ...prev,
          certificates: [...prev.certificates, { url: data.url, original_name: data.original_name }]
        }));
      }
      toast.success(`${files.length} certificado(s) enviado(s)!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar certificado');
    } finally {
      setCertUploading(false);
      if (certInputRef.current) certInputRef.current.value = '';
    }
  };

  const removeCertificate = (idx) => {
    setForm(prev => ({
      ...prev,
      certificates: prev.certificates.filter((_, i) => i !== idx)
    }));
  };

  const canProceed = () => {
    switch (step) {
      case 0: return form.name && form.email && form.password && form.password === form.confirmPassword;
      case 1: return form.professional_type !== '';
      case 2: return form.specializations.length > 0 && form.approach.length > 0 && form.bio && form.certificates.length > 0;
      case 3: return true;
      case 4: return form.hourly_rate > 0;
      default: return false;
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const profData = {
        email: form.email,
        password: form.password,
        name: form.name,
        phone: form.phone || null,
        professional_type: form.professional_type,
        specializations: form.specializations,
        approach: form.approach,
        bio: form.bio,
        experience_years: parseInt(form.experience_years) || 0,
        hourly_rate: parseFloat(form.hourly_rate) || 39.90,
        photo_url: form.photo_url || null,
        certificates: form.certificates
      };
      const result = await registerProfessional(profData);
      if (result.success) {
        toast.success('Cadastro realizado com sucesso!');
        navigate('/dashboard');
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Erro ao cadastrar');
    } finally {
      setSubmitting(false);
    }
  };

  const specs = allSpecializations[form.professional_type] || [];
  const apps = allApproaches[form.professional_type] || [];

  return (
    <div className="min-h-screen flex flex-col bg-[#F9F8F6]">
      <Header />
      <main className="flex-1 py-12" data-testid="register-professional-page">
        <div className="max-w-2xl mx-auto px-6">
          <Link to="/register" className="inline-flex items-center gap-2 text-[#63635E] hover:text-[#292927] mb-6">
            <ArrowLeft className="w-4 h-4" /> Voltar
          </Link>

          <h1 className="text-3xl font-light text-[#292927] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Cadastro Profissional
          </h1>
          <p className="text-[#63635E] mb-8">Preencha suas informações para começar a atender</p>

          {/* Progress */}
          <div className="flex items-center gap-1 mb-8">
            {regSteps.map((s, idx) => (
              <div key={s.id} className={`flex-1 h-1.5 rounded-full transition-colors ${idx <= step ? 'bg-[#4A5D4E]' : 'bg-[#EAE8E3]'}`} />
            ))}
          </div>
          <p className="text-sm text-[#A0A09A] mb-6">{step + 1} de {regSteps.length} — {regSteps[step].title}</p>

          {error && <div className="mb-6 p-4 rounded-xl bg-[#B85C5C]/10 text-[#B85C5C] text-sm">{error}</div>}

          <div className="card-theraflow p-8">
            {/* Step 0: Basic Info */}
            {step === 0 && (
              <div className="space-y-4" data-testid="step-basic">
                {/* Photo Upload */}
                <div className="flex flex-col items-center mb-4">
                  <Label className="mb-3 text-center">Foto de Perfil</Label>
                  <div
                    className="relative w-28 h-28 rounded-2xl border-2 border-dashed border-[#EAE8E3] hover:border-[#4A5D4E] bg-[#F9F8F6] flex items-center justify-center cursor-pointer transition-all overflow-hidden group"
                    onClick={() => photoInputRef.current?.click()}
                    data-testid="photo-upload-area"
                  >
                    {photoPreview || form.photo_url ? (
                      <img
                        src={photoPreview || (form.photo_url.startsWith('/') ? `${API_URL}${form.photo_url}` : form.photo_url)}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-center">
                        <Camera className="w-8 h-8 mx-auto text-[#A0A09A] group-hover:text-[#4A5D4E] transition-colors" strokeWidth={1.5} />
                        <p className="text-xs text-[#A0A09A] mt-1">Adicionar</p>
                      </div>
                    )}
                    {photoUploading && (
                      <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                        <Loader2 className="w-6 h-6 text-[#4A5D4E] animate-spin" />
                      </div>
                    )}
                  </div>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                  <p className="text-xs text-[#A0A09A] mt-2">JPG, PNG ou WebP (máx. 5MB)</p>
                </div>

                <div>
                  <Label>Nome completo *</Label>
                  <Input value={form.name} onChange={e => setField('name', e.target.value)} className="input-theraflow mt-1" placeholder="Dr. Maria Silva" data-testid="prof-name" />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={form.email} onChange={e => setField('email', e.target.value)} className="input-theraflow mt-1" placeholder="email@exemplo.com" data-testid="prof-email" />
                </div>
                <div>
                  <Label>Telefone</Label>
                  <Input value={form.phone} onChange={e => setField('phone', e.target.value)} className="input-theraflow mt-1" placeholder="(11) 99999-9999" data-testid="prof-phone" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Senha *</Label>
                    <Input type="password" value={form.password} onChange={e => setField('password', e.target.value)} className="input-theraflow mt-1" data-testid="prof-password" />
                  </div>
                  <div>
                    <Label>Confirmar senha *</Label>
                    <Input type="password" value={form.confirmPassword} onChange={e => setField('confirmPassword', e.target.value)} className="input-theraflow mt-1" data-testid="prof-confirm-password" />
                  </div>
                </div>
                {form.password && form.confirmPassword && form.password !== form.confirmPassword && (
                  <p className="text-sm text-[#B85C5C]">As senhas não conferem</p>
                )}
              </div>
            )}

            {/* Step 1: Professional Type */}
            {step === 1 && (
              <div className="space-y-4" data-testid="step-type">
                <p className="text-sm text-[#63635E] mb-2">Qual é a sua área de atuação principal?</p>
                {profTypes.map(t => (
                  <button
                    key={t.value}
                    onClick={() => { setField('professional_type', t.value); setField('specializations', []); setField('approach', []); }}
                    className={`w-full p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all ${
                      form.professional_type === t.value ? 'border-[#4A5D4E] bg-[#4A5D4E]/5' : 'border-[#EAE8E3] hover:border-[#4A5D4E]/40'
                    }`}
                    data-testid={`prof-type-${t.value}`}
                  >
                    <div className={`w-12 h-12 rounded-xl ${t.color} flex items-center justify-center`}>
                      <t.icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="font-medium text-[#292927]">{t.label}</p>
                      <p className="text-sm text-[#63635E]">{t.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Step 2: Qualifications */}
            {step === 2 && (
              <div className="space-y-6" data-testid="step-qualifications">
                <div>
                  <Label className="mb-2 block">Especializações * <span className="text-[#A0A09A]">(selecione todas)</span></Label>
                  <div className="flex flex-wrap gap-2">
                    {specs.map(s => (
                      <button key={s} onClick={() => toggleArray('specializations', s)}
                        className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                          form.specializations.includes(s) ? 'bg-[#4A5D4E] text-white' : 'bg-[#EAE8E3] text-[#63635E] hover:bg-[#D4A373]/20'
                        }`}
                      >{s}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Abordagens terapêuticas * <span className="text-[#A0A09A]">(selecione todas)</span></Label>
                  <div className="flex flex-wrap gap-2">
                    {apps.map(a => (
                      <button key={a} onClick={() => toggleArray('approach', a)}
                        className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                          form.approach.includes(a) ? 'bg-[#4A5D4E] text-white' : 'bg-[#EAE8E3] text-[#63635E] hover:bg-[#D4A373]/20'
                        }`}
                      >{a}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Bio / Apresentação *</Label>
                  <Textarea value={form.bio} onChange={e => setField('bio', e.target.value)}
                    className="input-theraflow mt-1 min-h-[100px]"
                    placeholder="Conte sobre sua formação, experiência e como você trabalha..."
                    data-testid="prof-bio"
                  />
                </div>
                <div>
                  <Label>Anos de experiência</Label>
                  <Input type="number" value={form.experience_years} onChange={e => setField('experience_years', e.target.value)}
                    className="input-theraflow mt-1 w-32" min="0" data-testid="prof-experience"
                  />
                </div>

                {/* Certificate Upload */}
                <div>
                  <Label className="mb-2 block flex items-center gap-2">
                    <Upload className="w-4 h-4 text-[#4A5D4E]" />
                    Certificados e diplomas *
                  </Label>
                  <p className="text-xs text-[#A0A09A] mb-3">
                    Envie seus certificados, diplomas ou comprovantes de formação. Aceitos: JPG, PNG, PDF (máx. 10MB cada).
                  </p>

                  {/* Uploaded Certificates */}
                  {form.certificates.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {form.certificates.map((cert, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-3 rounded-xl bg-[#4A5D4E]/5 border border-[#4A5D4E]/20">
                          <File className="w-5 h-5 text-[#4A5D4E] flex-shrink-0" strokeWidth={1.5} />
                          <span className="text-sm text-[#292927] flex-1 truncate">{cert.original_name}</span>
                          <CheckCircle className="w-4 h-4 text-[#6B8E7C] flex-shrink-0" />
                          <button
                            onClick={() => removeCertificate(idx)}
                            className="text-[#B85C5C] hover:text-red-700 flex-shrink-0"
                            data-testid={`remove-cert-${idx}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Upload Button */}
                  <button
                    type="button"
                    onClick={() => certInputRef.current?.click()}
                    disabled={certUploading}
                    className="w-full p-4 rounded-xl border-2 border-dashed border-[#EAE8E3] hover:border-[#4A5D4E] bg-[#F9F8F6] flex items-center justify-center gap-2 text-sm text-[#63635E] hover:text-[#4A5D4E] transition-all"
                    data-testid="cert-upload-button"
                  >
                    {certUploading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-5 h-5" strokeWidth={1.5} />
                        {form.certificates.length > 0 ? 'Adicionar mais certificados' : 'Enviar certificados'}
                      </>
                    )}
                  </button>
                  <input
                    ref={certInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    multiple
                    onChange={handleCertUpload}
                    className="hidden"
                  />
                  {form.certificates.length === 0 && (
                    <p className="text-xs text-[#B85C5C] mt-2">
                      Pelo menos um certificado é obrigatório para o cadastro
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Practice */}
            {step === 3 && (
              <div className="space-y-6" data-testid="step-practice">
                <div>
                  <Label className="mb-2 block flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#4A5D4E]" />
                    Experiência com casos similares
                  </Label>
                  <Textarea value={form.experience_cases} onChange={e => setField('experience_cases', e.target.value)}
                    className="input-theraflow mt-1 min-h-[80px]"
                    placeholder="Descreva sua experiência com os tipos de casos que mais atende..."
                  />
                </div>
                <div>
                  <Label className="mb-3 block flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#4A5D4E]" />
                    Preferência de atendimento
                  </Label>
                  <div className="flex gap-3">
                    {[{ id: 'online', label: 'Online' }, { id: 'both', label: 'Online e Presencial' }].map(opt => (
                      <button key={opt.id} onClick={() => setField('session_preference', opt.id)}
                        className={`flex-1 p-3 rounded-xl border-2 text-sm transition-all ${
                          form.session_preference === opt.id ? 'border-[#4A5D4E] bg-[#4A5D4E]/5' : 'border-[#EAE8E3]'
                        }`}
                      >{opt.label}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#4A5D4E]" />
                    Como você estabelece metas com seus clientes?
                  </Label>
                  <Textarea value={form.goal_methodology} onChange={e => setField('goal_methodology', e.target.value)}
                    className="input-theraflow mt-1 min-h-[80px]"
                    placeholder="Descreva sua metodologia para definir e acompanhar metas..."
                  />
                </div>
                <div className="flex items-center space-x-3">
                  <Checkbox id="collab" checked={form.open_collaboration} onCheckedChange={v => setField('open_collaboration', v)} />
                  <Label htmlFor="collab" className="cursor-pointer text-sm">
                    Estou aberto(a) a colaborar com outros profissionais e integrar métodos
                  </Label>
                </div>
              </div>
            )}

            {/* Step 4: Pricing */}
            {step === 4 && (
              <div className="space-y-6" data-testid="step-pricing">
                <p className="text-sm text-[#63635E]">Defina seus preços por modalidade (mínimo R$39,90)</p>
                <div>
                  <Label>Valor base por hora (R$) *</Label>
                  <Input type="number" value={form.hourly_rate} onChange={e => setField('hourly_rate', e.target.value)}
                    className="input-theraflow mt-1 w-40" min="39.90" step="0.10" placeholder="150.00"
                    data-testid="prof-hourly-rate"
                  />
                </div>
                <div>
                  <Label className="mb-3 block">Preços por modalidade (opcional)</Label>
                  <div className="grid grid-cols-2 gap-4">
                    {[{ key: 'chat', label: 'Chat' }, { key: 'video', label: 'Vídeo Call' }, { key: 'call', label: 'Ligação' }, { key: 'email', label: 'Email' }].map(m => (
                      <div key={m.key}>
                        <Label className="text-xs text-[#A0A09A]">{m.label} (R$)</Label>
                        <Input type="number" value={form.session_prices[m.key]}
                          onChange={e => setForm(prev => ({ ...prev, session_prices: { ...prev.session_prices, [m.key]: e.target.value } }))}
                          className="input-theraflow mt-1" min="39.90" step="0.10" placeholder="39.90"
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-[#F6EBE6]">
                  <p className="text-sm text-[#63635E]">
                    A plataforma retém 25% de comissão sobre cada sessão. Você recebe 75% do valor.
                  </p>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t border-[#EAE8E3]">
              <button onClick={() => setStep(s => s - 1)} disabled={step === 0}
                className={`flex items-center gap-2 text-sm font-medium ${step === 0 ? 'text-[#A0A09A]' : 'text-[#63635E] hover:text-[#292927]'}`}
              >
                <ArrowLeft className="w-4 h-4" /> Voltar
              </button>
              {step < regSteps.length - 1 ? (
                <button onClick={() => setStep(s => s + 1)} disabled={!canProceed()}
                  className="btn-primary flex items-center gap-2" data-testid="prof-next"
                >
                  Próximo <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button onClick={handleSubmit} disabled={submitting || !canProceed()}
                  className="btn-primary flex items-center gap-2" data-testid="prof-submit"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-4 h-4" /> Cadastrar</>}
                </button>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
